
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Smartphone,
  BarChart3,
  TrendingUp,
  Bell,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  Search,
  Filter,
  Calendar,
  Download,
  RefreshCw,
  LineChart,
  X,
  PieChart,
  Activity,
} from 'lucide-react';
import {
  LineChart as RechartsLineChart,
  BarChart as RechartsBarChart,
  PieChart as RechartsPieChart,
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Bar,
  Cell,
  Pie,
  Line,
} from 'recharts';

// Full Screen Graph View Component
const GraphView = ({
  onBack,
  statsData,
  selectedApp,
  selectedGraphType,
  setSelectedGraphType,
  activeTab
}) => {
  if (!statsData) return null;

  // Prepare data for pie chart
  const getOverviewPieData = () => {
    return [
      { name: 'Delivered', value: statsData.delivered || 0, color: '#10B981' },
      { name: 'Failed', value: statsData.failed || 0, color: '#EF4444' },
      { name: 'Pending', value: statsData.pending || 0, color: '#F59E0B' },
    ].filter(item => item.value > 0); // Filter out zero values to avoid clutter
  };

  // Prepare data for bar chart
  const getTopicsBarData = () => {
    return statsData.topTopics.map((topic) => ({
      name: topic.name.replace('_', ' ').toUpperCase(),
      count: topic.count,
    }));
  };

  const renderSelectedGraph = () => {
    const commonTooltipStyle = {
      backgroundColor: '#1F2937',
      border: '1px solid #374151',
      borderRadius: '8px',
      color: '#F9FAFB',
    };

    switch (selectedGraphType) {
      case 'daily-trend':
        return (
          <div className="h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={statsData.dailyStats} margin={{ top: 40, right: 40, left: 40, bottom: 40 }}>
                <defs>
                  <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorDelivered" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorFailed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#9CA3AF"
                  fontSize={12}
                  tickMargin={10}
                />
                <YAxis
                  stroke="#9CA3AF"
                  fontSize={12}
                  tickMargin={10}
                />
                <Tooltip contentStyle={commonTooltipStyle} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="sent"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorSent)"
                  name="Sent"
                />
                <Area
                  type="monotone"
                  dataKey="delivered"
                  stroke="#10B981"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorDelivered)"
                  name="Delivered"
                />
                <Area
                  type="monotone"
                  dataKey="failed"
                  stroke="#EF4444"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorFailed)"
                  name="Failed"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        );

      case 'status-pie':
        return (
          <div className="h-full w-full flex items-center justify-center">
            <div className="w-full max-w-[500px] h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={getOverviewPieData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius="80%"
                    dataKey="value"
                  >
                    {getOverviewPieData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={commonTooltipStyle} />
                  <Legend />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>
        );

        return (
          <div className="h-full w-full flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart margin={{ top: 40, right: 40, left: 40, bottom: 40 }}>
                <Pie
                  data={getOverviewPieData()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={200}
                  fill="#8884d8"
                  dataKey="value"
                  fontSize={14}
                >
                  {getOverviewPieData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={commonTooltipStyle} />
                <Legend />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        );

      case 'topics-bar':
        return (
          <div className="h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsBarChart data={getTopicsBarData()} margin={{ top: 40, right: 40, left: 40, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="name"
                  stroke="#9CA3AF"
                  fontSize={12}
                  tickMargin={10}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  stroke="#9CA3AF"
                  fontSize={12}
                  tickMargin={10}
                />
                <Tooltip contentStyle={commonTooltipStyle} />
                <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        );

      case 'delivery-line':
        return (
          <div className="h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsLineChart data={statsData.dailyStats} margin={{ top: 40, right: 40, left: 40, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#9CA3AF"
                  fontSize={12}
                  tickMargin={10}
                />
                <YAxis
                  stroke="#9CA3AF"
                  fontSize={12}
                  tickMargin={10}
                />
                <Tooltip contentStyle={commonTooltipStyle} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="delivered"
                  stroke="#10B981"
                  strokeWidth={4}
                  dot={{ fill: '#10B981', strokeWidth: 2, r: 8 }}
                  name="Delivered"
                />
                <Line
                  type="monotone"
                  dataKey="failed"
                  stroke="#EF4444"
                  strokeWidth={4}
                  dot={{ fill: '#EF4444', strokeWidth: 2, r: 8 }}
                  name="Failed"
                />
              </RechartsLineChart>
            </ResponsiveContainer>
          </div>
        );

      default:
        return null;
    }
  };

  const graphTypes = [
    { id: 'daily-trend', label: 'Daily Trend', icon: Activity, color: 'blue' },
    { id: 'status-pie', label: 'Status Distribution', icon: PieChart, color: 'green' },
    ...(activeTab === 'push'
      ? [{ id: 'topics-bar', label: 'Top Topics', icon: BarChart3, color: 'purple' }]
      : []),
    { id: 'delivery-line', label: 'Delivery Rate', icon: TrendingUp, color: 'orange' },
  ];


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-white/5 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="p-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors text-white backdrop-blur-sm border border-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center">
                  <LineChart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">Graph Analytics</h1>
                  <p className="text-white/60">Visual insights for {selectedApp}</p>
                </div>
              </div>
            </div>

            {/* <div className="flex items-center space-x-3">
              <button className="flex items-center space-x-2 px-4 py-2 bg-green-500/20 text-green-200 rounded-xl hover:bg-green-500/30 transition-all duration-200">
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div> */}
          </div>
        </div>
      </div>

      {/* Graph Type Selector */}
      <div className="bg-white/5 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-wrap gap-4">
            {graphTypes.map((graph) => {
              const Icon = graph.icon;
              const isSelected = selectedGraphType === graph.id;

              return (
                <button
                  key={graph.id}
                  onClick={() => setSelectedGraphType(graph.id)}
                  className={`flex items-center space-x-3 px-6 py-4 rounded-xl transition-all duration-200 ${isSelected
                    ? 'bg-blue-500/20 border border-blue-500/50 text-blue-200 scale-105'
                    : 'bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-white/10 hover:border-white/20'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium text-lg">{graph.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Graph Content - Full height remaining space */}
      <div className="h-[calc(100vh-200px)]">
        <div className="max-w-7xl mx-auto px-6 py-6 h-full">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 h-full">
            {renderSelectedGraph()}
          </div>
        </div>
      </div>

      {/* Footer Stats */}
      <div className="bg-white/5 backdrop-blur-xl border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="grid grid-cols-4 gap-6 text-center">
            <div className="p-4 bg-white/5 rounded-2xl border border-white/10">
              <p className="text-white/60 text-sm mb-1">Total Sent</p>
              <p className="text-white font-bold text-2xl">{statsData.totalNotifications.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-white/5 rounded-2xl border border-white/10">
              <p className="text-white/60 text-sm mb-1">Delivered</p>
              <p className="text-green-400 font-bold text-2xl">{statsData.delivered.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-white/5 rounded-2xl border border-white/10">
              <p className="text-white/60 text-sm mb-1">Failed</p>
              <p className="text-red-400 font-bold text-2xl">{statsData.failed.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-white/5 rounded-2xl border border-white/10">
              <p className="text-white/60 text-sm mb-1">Success Rate</p>
              <p className="text-blue-400 font-bold text-2xl">{(statsData.deliveryRate * 100).toFixed(1)}%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GraphView;